import { ReportResult } from "@/features/reporting/interfaces/result";
import { TemplateListItem } from "@/features/reporting/interfaces/report";
import type { ScheduleFrequency, EveryUnit } from "@/features/reporting/interfaces/common";

const MAX_SEARCH_LENGTH = 500;
export { MAX_SEARCH_LENGTH };

export const MAX_EMAIL_LENGTH = 2500;

export const SCHEDULE_NAME_MAX_LENGTH = 50;

/** Min/max for "Every" recurrence (Daily 1–6 days, Weekly 1–6 weeks, Monthly 1–6 months) and Monthly "On" day. */
export const RECURRENCE_MIN = 1;
export const RECURRENCE_MAX = 6;

export const WEEKDAY_KEYS = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"] as const;

export const RECURRENCE_OPTIONS_1_TO_6 = [1, 2, 3, 4, 5, 6] as const;

export const FREQUENCY_OPTIONS: { value: ScheduleFrequency; label: string }[] = [
  { value: "DAILY", label: "CENTRALISED_REPORTING.CREATE_SCHEDULE.FREQUENCY_DAILY" },
  { value: "WEEKLY", label: "CENTRALISED_REPORTING.CREATE_SCHEDULE.FREQUENCY_WEEKLY" },
  { value: "MONTHLY", label: "CENTRALISED_REPORTING.CREATE_SCHEDULE.FREQUENCY_MONTHLY" }
];

export const EVERY_UNIT_OPTIONS: { value: EveryUnit; label: string }[] = [
  { value: "DAYS", label: "CENTRALISED_REPORTING.CREATE_SCHEDULE.EVERY_UNIT_DAYS" },
  { value: "WEEKS", label: "CENTRALISED_REPORTING.CREATE_SCHEDULE.EVERY_UNIT_WEEKS" },
  { value: "MONTHS", label: "CENTRALISED_REPORTING.CREATE_SCHEDULE.EVERY_UNIT_MONTHS" }
];

const SCHEDULES_TABLE_COLUMNS = [
  { id: "onOff", key: "onOff", width: "62px", labelKey: "CENTRALISED_REPORTING.SCHEDULES_TABLE.ON_OFF" },
  { id: "frequency", key: "frequency", width: "140px", labelKey: "CENTRALISED_REPORTING.SCHEDULES_TABLE.FREQUENCY" },
  { id: "expiry", key: "expiry", width: "94px", labelKey: "CENTRALISED_REPORTING.SCHEDULES_TABLE.EXPIRY" },
  { id: "sentTo", key: "sentTo", width: "180px", labelKey: "CENTRALISED_REPORTING.SCHEDULES_TABLE.SENT_TO" },
  { id: "actions", key: "actions", width: "80px", labelKey: "CENTRALISED_REPORTING.SCHEDULES_TABLE.ACTIONS" }
] as const;

/** Returns schedule table columns with resolved i18n labels for mc-table (same pattern as getReportsColumns). */
export const getSchedulesTableColumns = (t: (key: string) => string) =>
  SCHEDULES_TABLE_COLUMNS.map((col) => ({
    id: col.id,
    label: t(col.labelKey),
    key: col.key,
    width: col.width,
    sortDisabled: true
  }));

export const DAY_OF_WEEK_OPTION_KEYS: { value: string; label: string }[] = [
  { value: "SUNDAY", label: "CENTRALISED_REPORTING.CREATE_SCHEDULE.DAY_SUNDAY" },
  { value: "MONDAY", label: "CENTRALISED_REPORTING.CREATE_SCHEDULE.DAY_MONDAY" },
  { value: "TUESDAY", label: "CENTRALISED_REPORTING.CREATE_SCHEDULE.DAY_TUESDAY" },
  { value: "WEDNESDAY", label: "CENTRALISED_REPORTING.CREATE_SCHEDULE.DAY_WEDNESDAY" },
  { value: "THURSDAY", label: "CENTRALISED_REPORTING.CREATE_SCHEDULE.DAY_THURSDAY" },
  { value: "FRIDAY", label: "CENTRALISED_REPORTING.CREATE_SCHEDULE.DAY_FRIDAY" },
  { value: "SATURDAY", label: "CENTRALISED_REPORTING.CREATE_SCHEDULE.DAY_SATURDAY" }
];
export const DEFAULT_PAGE_SIZE_RESULTS_TABLE = 10;
export interface KeyValueInterface {
  key: string;
  value: string;
}
export type AppearanceType = "error" | "warning" | "success" | "neutral-weak" | "info";
export type DefaultAppearanceType = "neutral";
const formatCount = (count: number): string => {
  if (count == null || count < 0 || !Number.isFinite(count)) {
    return "-";
  }
  return count.toLocaleString();
};
const formatDate = (dateString: string): string => {
  if (!dateString || dateString.trim() === "") {
    return "-";
  }
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  // Example: 23 Mar 2026, 12:48:06 (local timezone)
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
};

const getSafeDisplayValue = (report: ReportResult | TemplateListItem, field: string): string => {
  const value = report[field as keyof ReportResult];

  if (value === null || value === undefined) {
    return "N/A";
  }

  if (typeof value === "string") {
    return value.trim() || "N/A";
  }

  if (isKeyValueInterface(value)) {
    return value.value?.trim() || "N/A";
  }

  return String(value);
};

/**
 * Type guard function to check if a value is a KeyValueInterface
 * @param value - The value to check
 * @returns True if the value is a KeyValueInterface
 */
export const isKeyValueInterface = (value: unknown): value is KeyValueInterface => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const obj = value as Record<string, unknown>;
  return typeof obj.key === "string" && typeof obj.value === "string";
};

const POSSIBLE_LABEL_KEYS_FOR_FILTER = ["name", "label", "countryName", "portName", "value"];
const POSSIBLE_VALUE_KEYS_FOR_FILTER = ["code", "key", "countryCode", "portCode", "id"];
export { formatDate, formatCount, getSafeDisplayValue, POSSIBLE_LABEL_KEYS_FOR_FILTER, POSSIBLE_VALUE_KEYS_FOR_FILTER };
